import moment from 'moment';

/**
 * Finds the previous financial year from the current financial year
 * @param {number} currentFinancialYearId - Current financial year ID
 * @param {Array} listFYData - List of all financial years
 * @returns {Object|null} Previous financial year object or null if not found
 */
export const findPreviousFinancialYear = (currentFinancialYearId, listFYData = []) => {
  if (!currentFinancialYearId || !listFYData?.length) return null;

  const currentFY = listFYData.find((fy) => fy.financialyearid === currentFinancialYearId);
  if (!currentFY) return null;

  const prevStartYear = Number(currentFY.startyear) - 1;
  const prevEndYear = Number(currentFY.endyear) - 1;

  // Try to find by matching companyid first, then fallback to any match
  const prevFY =
    listFYData.find(
      (f) =>
        Number(f.startyear) === prevStartYear &&
        Number(f.endyear) === prevEndYear &&
        (currentFY.companyid ? f.companyid === currentFY.companyid : true)
    ) || listFYData.find((f) => Number(f.startyear) === prevStartYear && Number(f.endyear) === prevEndYear);

  return prevFY || null;
};

/**
 * Determines the period type and value from the current period selection
 * @param {Object} periodData - Period data with startdate, enddate, financialyearid
 * @param {Array} listFYData - List of all financial years
 * @returns {Object|null} Object with periodType and periodValue, or null if cannot determine
 */
export const determinePeriodType = (periodData, listFYData = []) => {
  if (!periodData?.startdate || !periodData?.enddate || !periodData?.financialyearid) return null;

  const fy = listFYData.find((f) => f.financialyearid === periodData.financialyearid);
  if (!fy) return null;

  const start = moment.unix(periodData.startdate);
  const end = moment.unix(periodData.enddate);

  // Check if it's Full Year
  if (periodData.startdate === fy.startdate && periodData.enddate === fy.enddate) {
    return { periodType: 'fullyear', periodValue: 'Full Year' };
  }

  // Check if it's a single month
  if (start.isSame(end, 'month') && start.isSame(start.clone().startOf('month'), 'day') && end.isSame(end.clone().endOf('month'), 'day')) {
    return { periodType: 'monthly', periodValue: start.format('MMMM') };
  }

  // Calculate timestamps once for reuse
  const startTs = start.startOf('day').unix();
  const endTs = end.endOf('day').unix();

  // Check if it's a quarter (matching the getDynamicQuarters logic)
  // Quarters are calculated from FY start using calendar quarters
  const fyStart = moment.unix(fy.startdate);
  const fyStartQuarter = fyStart.clone().startOf('quarter');
  
  // Check if the period matches a quarter boundary
  const quarterStart = start.clone().startOf('quarter');
  const quarterEnd = start.clone().endOf('quarter');
  
  // Compare timestamps to avoid time-of-day issues
  const quarterStartTs = quarterStart.startOf('day').unix();
  const quarterEndTs = quarterEnd.endOf('day').unix();
  
  if (startTs === quarterStartTs && endTs === quarterEndTs) {
    // Calculate which quarter number this is (1-based) from FY start
    const monthsFromFYStart = start.diff(fyStartQuarter, 'months', true);
    const quarterNum = Math.floor(monthsFromFYStart / 3) + 1;
    
    // Verify this is a valid quarter (should be positive and within FY)
    if (quarterNum > 0 && quarterNum <= 4) {
      return { periodType: 'quarterly', periodValue: `Q${quarterNum}` };
    }
  }

  // Check if it's half year
  // H1: First 6 months from FY start
  // H2: Next 6 months (months 7-12)
  const fyStartMoment = moment.unix(fy.startdate);
  const h1Start = fyStartMoment.clone().startOf('month');
  const h1End = h1Start.clone().add(5, 'months').endOf('month');
  const h2Start = h1End.clone().add(1, 'day').startOf('month');
  const h2End = h2Start.clone().add(5, 'months').endOf('month');

  // Compare timestamps to avoid time-of-day issues (reusing startTs and endTs)
  const h1StartTs = h1Start.startOf('day').unix();
  const h1EndTs = h1End.endOf('day').unix();
  const h2StartTs = h2Start.startOf('day').unix();
  const h2EndTs = h2End.endOf('day').unix();

  if (startTs === h1StartTs && endTs === h1EndTs) {
    return { periodType: 'halfyearly', periodValue: 'H1' };
  }
  if (startTs === h2StartTs && endTs === h2EndTs) {
    return { periodType: 'halfyearly', periodValue: 'H2' };
  }

  // If none match, it's custom
  return { periodType: 'custom', periodValue: null };
};

/**
 * Gets the corresponding period dates in the previous financial year
 * @param {Object} currentPeriod - Current period data
 * @param {Array} listFYData - List of all financial years
 * @returns {Object|null} Object with startdate, enddate, financialyearid, companyid or null
 */
export const getPreviousYearPeriod = (currentPeriod, listFYData = []) => {
  if (!currentPeriod?.startdate || !currentPeriod?.enddate || !currentPeriod?.financialyearid) return null;

  const prevFY = findPreviousFinancialYear(currentPeriod.financialyearid, listFYData);
  if (!prevFY) return null;

  const periodInfo = determinePeriodType(currentPeriod, listFYData);
  if (!periodInfo) return null;

  const { periodType, periodValue } = periodInfo;

  // Handle Full Year
  if (periodType === 'fullyear') {
    return {
      startdate: prevFY.startdate,
      enddate: prevFY.enddate,
      financialyearid: prevFY.financialyearid,
      companyid: prevFY.companyid,
      openingbalance: currentPeriod.openingbalance || 1,
    };
  }

  // Handle Monthly
  if (periodType === 'monthly' && periodValue) {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    
    // Calculate how many months from the start of the current FY this month is
    // This gives us the position (0 = first month, 1 = second month, etc.)
    const monthsFromFYStart = currentStart.diff(currentFYStart, 'months', true);
    const monthOffset = Math.floor(monthsFromFYStart);

    // Apply the same offset to the previous FY
    const prevFYStart = moment.unix(prevFY.startdate);
    const prevMonthStart = prevFYStart.clone().add(monthOffset, 'months').startOf('month');
    const prevMonthEnd = prevMonthStart.clone().endOf('month');

    // Ensure it's within the previous FY bounds
    if (prevMonthStart.unix() >= prevFY.startdate && prevMonthEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevMonthStart.unix(),
        enddate: prevMonthEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  // Handle Quarterly
  if (periodType === 'quarterly' && periodValue) {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    const currentFYStartQuarter = currentFYStart.clone().startOf('quarter');
    
    // Calculate which quarter this is (0-based from FY start quarter)
    const monthsFromFYStartQuarter = currentStart.diff(currentFYStartQuarter, 'months', true);
    const quartersFromFYStartQuarter = Math.floor(monthsFromFYStartQuarter / 3);

    // Apply same offset to previous FY
    const prevFYStart = moment.unix(prevFY.startdate);
    const prevFYStartQuarter = prevFYStart.clone().startOf('quarter');
    const prevQuarterStart = prevFYStartQuarter.clone().add(quartersFromFYStartQuarter * 3, 'months').startOf('quarter');
    const prevQuarterEnd = prevQuarterStart.clone().endOf('quarter');

    // Ensure the quarter is within previous FY bounds
    if (prevQuarterStart.unix() >= prevFY.startdate && prevQuarterEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevQuarterStart.unix(),
        enddate: prevQuarterEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  // Handle Half Yearly
  if (periodType === 'halfyearly' && periodValue) {
    const prevFYStart = moment.unix(prevFY.startdate);
    let prevPeriodStart, prevPeriodEnd;

    if (periodValue === 'H1') {
      // H1: First 6 months from previous FY start
      prevPeriodStart = prevFYStart.clone().startOf('month');
      prevPeriodEnd = prevPeriodStart.clone().add(5, 'months').endOf('month');
    } else if (periodValue === 'H2') {
      // H2: Next 6 months (months 7-12 from FY start)
      prevPeriodStart = prevFYStart.clone().add(6, 'months').startOf('month');
      prevPeriodEnd = prevPeriodStart.clone().add(5, 'months').endOf('month');
    }

    // Ensure dates are within previous FY bounds
    if (prevPeriodStart && prevPeriodEnd && prevPeriodStart.unix() >= prevFY.startdate && prevPeriodEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevPeriodStart.unix(),
        enddate: prevPeriodEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  // Handle Custom Date - calculate same relative date range in previous year
  if (periodType === 'custom') {
    const currentStart = moment.unix(currentPeriod.startdate);
    const currentEnd = moment.unix(currentPeriod.enddate);
    const currentFY = listFYData.find((f) => f.financialyearid === currentPeriod.financialyearid);
    if (!currentFY) return null;

    const currentFYStart = moment.unix(currentFY.startdate);
    const currentFYEnd = moment.unix(currentFY.enddate);
    
    // Calculate the relative position within the current FY (as a percentage)
    const currentFYDuration = currentFYEnd.diff(currentFYStart, 'days');
    const daysFromFYStart = currentStart.diff(currentFYStart, 'days');
    const daysFromFYEnd = currentFYEnd.diff(currentEnd, 'days');
    
    // Calculate the duration of the custom period
    const periodDuration = currentEnd.diff(currentStart, 'days');

    // Apply the same relative positioning to the previous FY
    const prevFYStart = moment.unix(prevFY.startdate);
    const prevFYEnd = moment.unix(prevFY.enddate);
    const prevFYDuration = prevFYEnd.diff(prevFYStart, 'days');

    // Calculate start date in previous FY (maintain relative position)
    const relativeStartPosition = daysFromFYStart / currentFYDuration;
    const prevPeriodStart = prevFYStart.clone().add(Math.floor(prevFYDuration * relativeStartPosition), 'days');
    
    // Calculate end date maintaining the same duration
    const prevPeriodEnd = prevPeriodStart.clone().add(periodDuration, 'days');

    // Ensure dates are within previous FY bounds
    if (prevPeriodStart.unix() >= prevFY.startdate && prevPeriodEnd.unix() <= prevFY.enddate) {
      return {
        startdate: prevPeriodStart.unix(),
        enddate: prevPeriodEnd.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }

    // Fallback: if relative positioning doesn't work, try to maintain same month/day pattern
    const currentStartMonth = currentStart.month();
    const currentStartDate = currentStart.date();
    const currentEndMonth = currentEnd.month();
    const currentEndDate = currentEnd.date();

    // Try to find the same month/day in previous year
    const prevStartCandidate = prevFYStart.clone().month(currentStartMonth).date(currentStartDate);
    const prevEndCandidate = prevFYStart.clone().month(currentEndMonth).date(currentEndDate);

    // Adjust if dates are out of bounds
    if (prevStartCandidate.unix() < prevFY.startdate) {
      prevStartCandidate.add(1, 'year');
    }
    if (prevEndCandidate.unix() < prevFY.startdate) {
      prevEndCandidate.add(1, 'year');
    }
    if (prevStartCandidate.unix() > prevFY.enddate) {
      prevStartCandidate.subtract(1, 'year');
    }
    if (prevEndCandidate.unix() > prevFY.enddate) {
      prevEndCandidate.subtract(1, 'year');
    }

    // Ensure both dates are within bounds and start < end
    if (
      prevStartCandidate.unix() >= prevFY.startdate &&
      prevStartCandidate.unix() <= prevFY.enddate &&
      prevEndCandidate.unix() >= prevFY.startdate &&
      prevEndCandidate.unix() <= prevFY.enddate &&
      prevStartCandidate.unix() <= prevEndCandidate.unix()
    ) {
      return {
        startdate: prevStartCandidate.unix(),
        enddate: prevEndCandidate.unix(),
        financialyearid: prevFY.financialyearid,
        companyid: prevFY.companyid,
        openingbalance: currentPeriod.openingbalance || 1,
      };
    }
  }

  return null;
};

